import type { SessionAccount } from "@mathpop/contracts";
import { api } from "../services/api.js";

export class MpAuthPanel extends HTMLElement {
  account: SessionAccount | null = null;
  connectedCallback(): void {
    void this.load();
  }
  async load(): Promise<void> {
    try {
      this.account = (await api.session()).account;
      this.render();
    } catch {
      this.textContent = "Account unavailable. Refresh to try again.";
    }
  }
  render(): void {
    this.innerHTML = `<section class="panel sso-panel"><h1>MathPop account</h1><p data-summary></p><p class="form-status" role="status"></p><div data-actions class="sso-actions"></div></section>`;
    this.querySelector("[data-summary]")!.textContent = this.account
      ? `Signed in as ${this.account.profiles[0]?.nickname ?? this.account.displayName}. Sign-in and recovery are managed by Authentik.`
      : "Sign in with your family Authentik account. Ask Craig for an invitation if you need an account.";
    const actions = this.querySelector("[data-actions]")!;
    const link = (label: string, href: string) => {
      const a = document.createElement("a");
      a.className = "start-button";
      a.textContent = label;
      a.href = href;
      actions.append(a, document.createElement("br"));
    };
    const button = (label: string, action: () => Promise<void>) => {
      const b = document.createElement("button");
      b.className = "phrase-button";
      b.type = "button";
      b.textContent = label;
      b.onclick = async () => {
        b.disabled = true;
        try {
          await action();
        } catch {
          this.querySelector(".form-status")!.textContent =
            "That action could not finish. Please try again.";
        } finally {
          b.disabled = false;
        }
      };
      actions.append(b);
    };
    link(
      this.account ? "Back to the game" : "Sign in with Authentik",
      this.account ? "/" : "/api/v1/auth/oidc/login",
    );
    if (this.account) {
      button("Switch player", async () => {
        await api.logout();
        location.assign("/api/v1/auth/oidc/login?switch=1");
      });
      button("Sign out of MathPop", async () => {
        await api.logout();
        this.account = null;
        this.render();
      });
      button("Sign out other MathPop devices", async () => {
        const result = await api.revokeOtherSessions();
        this.querySelector(".form-status")!.textContent =
          `Signed out ${result.revoked} other devices.`;
      });
      const form = document.createElement("form");
      form.innerHTML =
        '<label>Player nickname <input name="nickname" maxlength="24" required></label><label><input name="board" type="checkbox"> Share verified scores on the public board</label><button>Save player settings</button>';
      const nickname = form.elements.namedItem("nickname") as HTMLInputElement,
        board = form.elements.namedItem("board") as HTMLInputElement;
      nickname.value =
        this.account.profiles[0]?.nickname ?? this.account.displayName;
      board.checked = this.account.profiles[0]?.leaderboardOptIn ?? false;
      form.onsubmit = async (e) => {
        e.preventDefault();
        form.dataset.releaseDirty = "true";
        const savedNickname = nickname.value,
          savedBoard = board.checked;
        try {
          this.account = (
            await api.updateProfile(savedNickname, savedBoard)
          ).account;
          this.querySelector(".form-status")!.textContent =
            "Player settings saved.";
          if (nickname.value === savedNickname && board.checked === savedBoard)
            delete form.dataset.releaseDirty;
        } catch {
          this.querySelector(".form-status")!.textContent =
            "Could not save settings. Please try again.";
        }
      };
      actions.append(form);
      button("Download my MathPop data", async () => {
        const data = await api.exportAccount();
        const url = URL.createObjectURL(
          new Blob([JSON.stringify(data, null, 2)], {
            type: "application/json",
          }),
        );
        const a = document.createElement("a");
        a.href = url;
        a.download = "mathpop-account.json";
        a.click();
        URL.revokeObjectURL(url);
      });
    }
    this.dispatchEvent(
      new CustomEvent("account-change", {
        detail: { account: this.account },
        bubbles: true,
        composed: true,
      }),
    );
  }
}
customElements.define("mp-auth-panel", MpAuthPanel);
